// Куда палитра умеет переходить: все экраны продукта одним списком.
//
// ## Почему список отдельный, а не собирается из разделов
//
// `app/sections.ts` знает только представление ПО УМОЛЧАНИЮ — то, куда ведёт полоса разделов.
// Остальные двадцать два экрана объявлены каждый в своём модуле, названиями в переключателе
// вкладок. Собрать их в одно место можно было бы только импортом из семи модулей, а это
// затащило бы в палитру половину продукта: она открывается по Ctrl+K на любом экране, и
// платить за неё загрузкой всех разделов нельзя.
//
// Значит список повторяет то, что есть в модулях, и повтор надо сторожить. Сторожит его
// `src/test/палитра-знает-все-экраны.test.ts`: он берёт список поверхностей сквозных проверок
// (`e2e/helpers.ts`, тот самый общий список, ради которого его и сделали общим) и требует,
// чтобы каждый экран оттуда нашёлся здесь. Завёл экран, забыл палитру — красное.
//
// ## Почему у каждой строки список прав, а не одно право
//
// Журнал действий открыт по двум разным правам: полному и «только свои действия». С одним
// правом в строке человек с урезанным журналом не нашёл бы его в палитре — при том что экран
// ему открывается.

import type { SectionSlug } from '@/app/sections';

export interface Адрес {
  /** Раздел — им же подписывается строка в палитре: «Расчёт · Ведомости». */
  readonly раздел: SectionSlug;
  /** Хвост адреса после раздела. */
  readonly представление: string;
  /** Название экрана ровно такое, как на его вкладке. */
  readonly название: string;
  /** Достаточно ЛЮБОГО из прав. */
  readonly права: readonly string[];
  /**
   * Слова, которыми человек может искать этот экран, не зная его названия.
   *
   * Это не украшение. Финансовый директор ищет «зарплата», а экран называется «Ведомости»;
   * ищет «аванс» — а это «Подотчёт». Без синонимов палитра отвечает «ничего не нашлось» на
   * правильный по смыслу запрос, и человек возвращается к мыши навсегда.
   */
  readonly синонимы: readonly string[];
}

export const АДРЕСА: readonly Адрес[] = [
  {
    раздел: 'money',
    представление: 'accountable',
    название: 'Подотчёт',
    права: ['core.accountable.view'],
    // «Суточные» и «суточные расходы» — слова заказчика: так называется раздел слева. Человек,
    // набравший название раздела, попадает на первый экран раздела, а не в «ничего не нашлось».
    синонимы: [
      'суточные',
      'суточные расходы',
      'деньги',
      'остаток',
      'выдать',
      'аванс',
      'долг',
      'пульт',
      'кто должен',
    ],
  },
  {
    раздел: 'money',
    представление: 'claims',
    название: 'Очередь приёмки',
    права: ['core.accountable.view'],
    синонимы: ['чеки', 'отчёты', 'принять', 'заявки', 'проверить чек'],
  },
  {
    раздел: 'money',
    представление: 'issues',
    название: 'Кому выдано',
    // Право то же, что у вкладки (`MoneySection.VIEW_PERMISSION`): реестр выдач — отчёт.
    права: ['core.report.view'],
    синонимы: ['кому выдали', 'выдали', 'выдачи', 'реестр выдач', 'получено в подотчёт'],
  },
  {
    раздел: 'money',
    представление: 'flow',
    название: 'На что потрачено',
    // Право то же, что у вкладки (`MoneySection.VIEW_PERMISSION`): отчёт требует право на
    // отчёты. С одним правом на подотчёт строка находилась, а вкладка за ней отказывала.
    права: ['core.report.view'],
    синонимы: ['расход', 'расходы', 'траты', 'потратили', 'итог', 'категории', 'на что потратили'],
  },
  {
    раздел: 'money',
    представление: 'archive',
    название: 'Архив переписки',
    // Право то же, что у вкладки: архив — уровень денег, а не переписки (авансы, зарплаты и
    // номера карт всей компании).
    права: ['core.chat_archive.view'],
    синонимы: ['переписка', 'вотсап', 'чаты', 'сообщения', 'что писали', 'старые чаты'],
  },
  {
    раздел: 'people',
    представление: 'employees',
    название: 'Реестр людей',
    права: ['core.employee.view'],
    синонимы: ['люди', 'сотрудники', 'охранники', 'кадры', 'персонал', 'штат'],
  },
  {
    раздел: 'sites',
    представление: 'objects',
    название: 'Объекты',
    права: ['core.dictionary.view'],
    синонимы: ['адреса', 'точки', 'площадки', 'где стоим'],
  },
  {
    раздел: 'sites',
    представление: 'customers',
    название: 'Заказчики',
    права: ['core.dictionary.view'],
    синонимы: ['клиенты', 'договоры', 'контрагенты'],
  },
  {
    раздел: 'sites',
    представление: 'posts',
    название: 'Посты',
    права: ['core.dictionary.view'],
    синонимы: ['смены', 'график', 'расстановка'],
  },
  {
    раздел: 'payroll',
    представление: 'sheets',
    название: 'Ведомости',
    права: ['core.payroll.view'],
    синонимы: ['зарплата', 'выплата', 'расчёт', 'к выплате', 'начисления'],
  },
  {
    раздел: 'payroll',
    представление: 'timesheet',
    название: 'Табель',
    права: ['core.timesheet.view'],
    синонимы: ['часы', 'отработано', 'смены', 'учёт времени'],
  },
  {
    раздел: 'payroll',
    представление: 'official',
    название: 'Официально',
    права: ['core.official.view'],
    синонимы: ['белая', 'налоги', 'приставы', 'удержания', 'контур'],
  },
  {
    раздел: 'payroll',
    представление: 'summary',
    название: 'Сводка',
    права: ['core.dashboard.view'],
    синонимы: ['итоги', 'за месяц', 'общая сумма'],
  },
  {
    раздел: 'payroll',
    представление: 'debts',
    название: 'Долги',
    права: ['core.debt.view'],
    синонимы: ['задолженность', 'висит', 'недостача'],
  },
  {
    раздел: 'payroll',
    представление: 'deductions',
    название: 'Удержания',
    // Вкладка открывается по двум правам сразу — ведомости и отчёты, — но у всех ролей, кому
    // открыты ведомости, открыты и отчёты. Одного права хватает, и оно точнее второго.
    права: ['core.payroll.view'],
    синонимы: ['авансы', 'билеты', 'форма', 'обучение', 'кому сколько удержали', 'вычеты'],
  },
  {
    раздел: 'payroll',
    представление: 'settlement',
    название: 'Расчёт сотрудника',
    // Право то же, что у вкладки и у её данных: расчёт человека собирается из его ведомостей.
    права: ['core.payroll.view'],
    // Слова финансового директора, когда человек уходит: он не ищет вкладку, он говорит «надо
    // рассчитать уволенного». Без этих слов палитра отвечала бы на них «ничего не нашлось», хотя
    // экран есть, и человек снова шёл бы по разделу мышью.
    синонимы: [
      'рассчитать сотрудника',
      'рассчитать уволенного',
      'расчёт уволенного',
      'окончательный расчёт',
      'расчёт при увольнении',
      'рассчитать человека',
      'итог по человеку',
    ],
  },
  {
    раздел: 'cases',
    представление: 'list',
    название: 'Дела',
    права: ['core.dashboard.view'],
    синонимы: ['задачи', 'что делать', 'очередь', 'просрочено'],
  },
  {
    раздел: 'settings',
    представление: 'general',
    название: 'Настройки',
    права: ['core.setting.view'],
    синонимы: ['сервер', 'адрес', 'пользователи', 'права', 'справочники'],
  },
  {
    раздел: 'settings',
    представление: 'audit',
    название: 'Журнал действий',
    // Два права: полный журнал и «только свои действия». См. заголовок файла.
    права: ['core.audit.view', 'core.audit.view_own'],
    синонимы: ['кто менял', 'история', 'аудит', 'кто что делал'],
  },
  {
    раздел: 'hiring',
    представление: 'candidates',
    название: 'Кандидаты',
    права: ['hiring.candidate.view'],
    синонимы: ['найм', 'соискатели', 'отклики', 'авито'],
  },
  {
    раздел: 'hiring',
    представление: 'funnel',
    название: 'Воронка',
    права: ['hiring.candidate.view'],
    синонимы: ['этапы', 'сколько дошло', 'аналитика найма'],
  },
  {
    раздел: 'hiring',
    представление: 'blacklist',
    название: 'Чёрный список',
    права: ['hiring.blacklist.view'],
    синонимы: ['не брать', 'запрет', 'чс'],
  },
  {
    раздел: 'hiring',
    представление: 'drafts',
    название: 'Предложения',
    права: ['hiring.blacklist.view'],
    синонимы: ['из выгрузки', 'добавить в чс'],
  },
  {
    раздел: 'hiring',
    представление: 'checks',
    название: 'Сверки',
    права: ['hiring.blacklist.view'],
    синонимы: ['журнал сверок', 'проверки чс'],
  },
  {
    раздел: 'hiring',
    представление: 'accounts',
    название: 'Площадка',
    права: ['hiring.avito.view'],
    синонимы: ['авито', 'учётные записи', 'вакансии'],
  },
  {
    раздел: 'hiring',
    представление: 'dialogs',
    название: 'Переписка',
    права: ['hiring.assistant.view'],
    синонимы: ['чаты', 'диалоги', 'помощник найма'],
  },
  {
    раздел: 'hiring',
    представление: 'library',
    название: 'Ответы помощника',
    права: ['hiring.assistant.view'],
    синонимы: ['библиотека', 'формулировки', 'шаблоны ответов'],
  },
];

/** Полный адрес экрана. */
export function путь(адрес: Адрес): string {
  return `/${адрес.раздел}/${адрес.представление}`;
}
